import { Colors } from '@/constants/theme';
import { Spacing, BorderRadius } from '@/constants/spacing';
import { useColorScheme } from '@/hooks/use-color-scheme';
import { Ionicons } from '@expo/vector-icons';
import { forwardRef, useImperativeHandle, useRef, type ReactNode } from 'react';
import { StyleSheet, TextInput, TouchableOpacity, View } from 'react-native';

interface ChatInputBarProps {
  input: string;
  isLoading: boolean;
  accentColor: string;
  backgroundColor: string;
  textColor: string;
  showDisclaimer?: boolean;
  placeholder?: string;
  maxLength?: number;
  /** Optional slot for the voice recording button */
  voiceButton?: ReactNode;
  /** Optional slot for the attachment (photo) button */
  attachmentButton?: ReactNode;
  /** Optional preview rendered above the input wrapper (e.g., staged image thumbnail) */
  attachmentPreview?: ReactNode;
  /** When true, allow submit even if text is empty — e.g., when a photo is staged. */
  allowEmptySubmit?: boolean;
  onChangeText: (text: string) => void;
  onSubmit: () => void;
}

export interface ChatInputBarRef {
  focus: () => void;
}

// 5.5 visible lines: a half-line peeking past the cap signals to the user
// that additional content exists below and the field becomes scrollable.
const MAX_LINES = 5.5;
const LINE_HEIGHT = 20;
const MIN_INPUT_HEIGHT = LINE_HEIGHT;
const MAX_INPUT_HEIGHT = LINE_HEIGHT * MAX_LINES;

export const ChatInputBar = forwardRef<ChatInputBarRef, ChatInputBarProps>(({
  input,
  isLoading,
  accentColor,
  backgroundColor,
  textColor,
  placeholder = 'Ask AI to organise something...',
  maxLength,
  voiceButton,
  attachmentButton,
  attachmentPreview,
  allowEmptySubmit = false,
  onChangeText,
  onSubmit,
}, ref) => {
  const colorScheme = useColorScheme();
  const palette = Colors[colorScheme ?? 'light'];
  const inputRef = useRef<TextInput>(null);

  useImperativeHandle(ref, () => ({
    focus: () => {
      inputRef.current?.focus();
    },
  }));

  const isSubmitDisabled = (!allowEmptySubmit && !input.trim()) || isLoading;

  return (
    <View style={styles.container}>
      {attachmentPreview}

      <View style={[styles.inputWrapper, { backgroundColor: palette.cardBackground, borderColor: palette.primary }]}>
        {attachmentButton}
        {voiceButton}

        <TextInput
          ref={inputRef}
          style={[styles.input, { color: textColor }]}
          placeholder={placeholder}
          placeholderTextColor={palette.textMuted}
          value={input}
          onChangeText={onChangeText}
          multiline
          textAlignVertical="top"
          editable={!isLoading}
          maxLength={maxLength}
        />
      </View>

      <View style={styles.buttonsRow}>
        <TouchableOpacity
          style={[styles.sendButton, { backgroundColor: isSubmitDisabled ? palette.borderMuted : palette.primary }]}
          onPress={onSubmit}
          disabled={isSubmitDisabled}
          activeOpacity={0.6}
        >
          <Ionicons name="arrow-up" size={20} color="#FFFFFF" />
        </TouchableOpacity>
      </View>
    </View>
  );
});

ChatInputBar.displayName = 'ChatInputBar';

const styles = StyleSheet.create({
  container: {
    gap: Spacing.sm,
  },
  inputWrapper: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: Spacing.lg,
    paddingVertical: Spacing.md,
    borderRadius: BorderRadius.lg,
    borderWidth: 1.5,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.2,
    shadowRadius: 12,
    elevation: 8,
  },
  input: {
    flex: 1,
    fontSize: 14,
    fontWeight: '700',
    lineHeight: LINE_HEIGHT,
    // Letting the multiline input grow naturally between min/max gives every
    // wrapped line space to render. Past MAX_INPUT_HEIGHT the field becomes
    // internally scrollable, which only happens for very long drafts.
    minHeight: MIN_INPUT_HEIGHT,
    maxHeight: MAX_INPUT_HEIGHT,
    paddingTop: 0,
    paddingBottom: 0,
  },
  buttonsRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 10,
  },
  sendButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    alignItems: 'center',
    justifyContent: 'center',
  },
});
